/**
 * Single source of truth for SAH participant-contribution *display labels*.
 *
 * Mobile previously guessed null percentages as "0%" and ignored hardship.
 * Web refuses to guess via fee-estimate / projectLineContribution. This helper
 * encodes the shared precedence for compact category labels used on care-worker
 * surfaces (and any web call site that only needs a short label).
 *
 * Precedence (mirrors apply_sah_service_financials + fee-estimate hardship):
 *   1. Clinical care → always $0 contribution (never "Not yet determined")
 *   2. Active hardship FULL_WAIVER / PARTIAL → hardship label wins over %
 *      (malformed hardship payload → "Not yet determined" — never fall back
 *      to the unreduced base rate; overstating contribution at the doorstep
 *      is the failure mode)
 *   3. null/undefined/non-finite/out-of-[0,100] percentage → "Not yet determined"
 *   4. Sliding/stored percentage → formatted %
 *   5. Pre-posting / estimate figures get an "Est." marker when isEstimate
 *
 * Citation: Aged Care Act 2024 Ch 4 contribution-display integrity;
 * Services Australia Fee Reduction (hardship) determinations.
 */

import { mapDbServiceCategoryToCanonical, type SahServiceCategory } from './sah';

export type ContributionHardshipKind = 'FULL_WAIVER' | 'PARTIAL';

export interface ContributionHardshipInput {
  reductionKind: ContributionHardshipKind;
  /** 0–100 for PARTIAL; ignored for FULL_WAIVER */
  reducedContributionPercent?: number | null;
}

export interface ContributionDisplayLabelInput {
  /**
   * Service category — accepts plan-spine tokens (CLINICAL_CARE / INDEPENDENCE /
   * EVERYDAY_LIVING), legacy lowercase, or canonical CLINICAL.
   */
  category?: string | null;
  /**
   * Stored contribution percentage in 0–100 units (inclusion % / assessed rate).
   * null/undefined means the means basis is not yet determined.
   * Values outside [0, 100] or non-finite are refused (not rendered as fact).
   */
  contributionPercentage?: number | null;
  /**
   * Active Services Australia hardship (Fee Reduction) determination.
   * When present for non-clinical categories it overrides the sliding %.
   * A present-but-unparseable payload refuses rather than falling back to base %.
   */
  activeHardship?: ContributionHardshipInput | null;
  /**
   * Pre-posting / projected figures must be labelled as estimates so workers
   * do not treat them as posted fact. Defaults to true.
   */
  isEstimate?: boolean;
  /**
   * Mobile frontline matrix: category language only — never % or $ amounts.
   * When true, percentage/hardship monetary detail is omitted.
   */
  categoryOnly?: boolean;
}

const CATEGORY_LABELS: Record<SahServiceCategory, string> = {
  CLINICAL: 'Clinical Care',
  INDEPENDENCE: 'Independence',
  EVERYDAY_LIVING: 'Everyday Living',
};

function resolveCategory(category: string | null | undefined): SahServiceCategory | null {
  if (!category) return null;
  const trimmed = category.trim();
  if (!trimmed) return null;
  const upper = trimmed.toUpperCase();
  if (upper === 'CLINICAL' || upper === 'CLINICAL_CARE') return 'CLINICAL';
  if (upper === 'INDEPENDENCE') return 'INDEPENDENCE';
  if (upper === 'EVERYDAY_LIVING' || upper === 'EVERYDAY') return 'EVERYDAY_LIVING';
  return mapDbServiceCategoryToCanonical(trimmed);
}

function formatPercent(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return `${rounded
    .toFixed(2)
    .replace(/\.00$/, '')
    .replace(/(\.\d)0$/, '$1')}%`;
}

function withEstimate(label: string, isEstimate: boolean): string {
  if (!isEstimate) return label;
  // Avoid double-prefixing if a caller already supplied Est.
  if (/^est\./i.test(label.trim())) return label;
  return `Est. ${label}`;
}

function notYetDetermined(categoryLabel: string, isEstimate: boolean): string {
  return withEstimate(`${categoryLabel} = Not yet determined`, isEstimate);
}

/**
 * Parse hardship into a display value, or null when the payload is present
 * but unparseable (caller must refuse — never fall back to unreduced base %).
 */
function hardshipValueLabel(hardship: ContributionHardshipInput): string | null {
  if (hardship.reductionKind === 'FULL_WAIVER') {
    return 'Full waiver (0%)';
  }
  if (hardship.reductionKind === 'PARTIAL') {
    const p = hardship.reducedContributionPercent;
    if (p == null || !Number.isFinite(p) || p < 0 || p > 100) {
      return null;
    }
    return `Reduced to ${formatPercent(p)}`;
  }
  // Unrecognised reduction kind — refuse.
  return null;
}

/**
 * Compact contribution label for a service category line.
 *
 * @example
 * contributionDisplayLabel({ category: 'INDEPENDENCE', contributionPercentage: null })
 * // → "Independence = Not yet determined"
 *
 * contributionDisplayLabel({
 *   category: 'EVERYDAY_LIVING',
 *   contributionPercentage: 50,
 *   activeHardship: { reductionKind: 'FULL_WAIVER' },
 * })
 * // → "Est. Everyday Living = Full waiver (0%)"
 */
export function contributionDisplayLabel(input: ContributionDisplayLabelInput): string {
  const isEstimate = input.isEstimate !== false;
  const category = resolveCategory(input.category);
  const categoryLabel = category ? CATEGORY_LABELS[category] : 'Contribution';

  // Frontline mobile matrix: category language only (no % / $).
  if (input.categoryOnly) {
    return categoryLabel;
  }

  // Clinical is always 0% government-funded — never "not yet determined".
  if (category === 'CLINICAL') {
    return withEstimate(`${categoryLabel} = $0 contribution`, isEstimate);
  }

  // Hardship present: parseable → hardship label; unparseable → refuse (no base-rate fallback).
  if (input.activeHardship) {
    const hardshipLabel = hardshipValueLabel(input.activeHardship);
    if (hardshipLabel) {
      return withEstimate(`${categoryLabel} = ${hardshipLabel}`, isEstimate);
    }
    return notYetDetermined(categoryLabel, isEstimate);
  }

  const pct = input.contributionPercentage;
  if (pct == null || !Number.isFinite(Number(pct))) {
    // Never render null as "0%" — that was the mobile defect.
    return notYetDetermined(categoryLabel, isEstimate);
  }

  const n = Number(pct);
  // Refuse corrupt out-of-range percentages rather than render them as fact.
  if (n < 0 || n > 100) {
    return notYetDetermined(categoryLabel, isEstimate);
  }

  return withEstimate(`${categoryLabel} = ${formatPercent(n)}`, isEstimate);
}
